// APP-1487 R32 (doc-security-market-data-01a150c533210e70): the cash flows of
// an individual bond for $100 of par, from the coupon terms of
// `/bonds/<CUSIP>/description`.

import { isEmptyValue } from "./fastTrackValues";

/** One payment of the bond for $100 of par, on a UTC-midnight date. */
export type BondCashFlow = {
  date: Date;
  /** The coupon, plus the $100 of par on the maturity date. */
  amount: number;
};

/** The par the cash flows are for (R32). */
export const PAR = 100;

/** The most payments the schedule lists: 100 years of monthly coupons. */
const MAX_PAYMENTS = 1200;

/**
 * A FastTrack date as a UTC midnight: `YYYYMMDD`, `YYYY-MM-DD` (with or
 * without a time) or `MM/DD/YYYY`. Null for an empty value or a date that
 * does not exist.
 */
export function parseBondTermDate(value: unknown): Date | null {
  if (isEmptyValue(value) || typeof value !== "string") return null;
  const text = value.trim();
  const match =
    text.match(/^(\d{4})(\d{2})(\d{2})$/) ??
    text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/);
  const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const [year, month, day] = match
    ? [Number(match[1]), Number(match[2]), Number(match[3])]
    : us
      ? [Number(us[3]), Number(us[1]), Number(us[2])]
      : [NaN, NaN, NaN];
  if (!Number.isInteger(year)) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? date
    : null;
}

/**
 * The coupons a year of a FastTrack `payment_frequency`: 12, 4, 2 or 1, 0
 * for a bond that pays only at maturity, null for a value it cannot read.
 */
export function paymentsPerYear(value: unknown): number | null {
  if (isEmptyValue(value)) return null;
  const text = String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  if (/^\d+$/.test(text)) {
    const number = Number(text);
    return [0, 1, 2, 4, 12].includes(number) ? number : null;
  }
  const words: Record<string, number> = {
    m: 12,
    monthly: 12,
    q: 4,
    quarterly: 4,
    s: 2,
    sa: 2,
    semiannual: 2,
    semiannually: 2,
    a: 1,
    annual: 1,
    annually: 1,
    yearly: 1,
    atmaturity: 0,
    maturity: 0,
    none: 0,
    zero: 0,
    zerocoupon: 0,
  };
  return words[text] ?? null;
}

/**
 * The date `months` months before `maturity`, on the maturity's day of the
 * month, or on the last day of a shorter month. A maturity on the last day
 * of its month keeps its coupons on the last day of each month.
 */
function monthsBefore(maturity: Date, months: number): Date {
  const year = maturity.getUTCFullYear();
  const month = maturity.getUTCMonth() - months;
  const day = maturity.getUTCDate();
  const lastDay = (y: number, m: number) =>
    new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const endOfMonth = day === lastDay(year, maturity.getUTCMonth());
  const target = lastDay(year, month);
  return new Date(
    Date.UTC(year, month, endOfMonth ? target : Math.min(day, target)),
  );
}

/**
 * Each cash flow of the bond after `asOf` to its maturity, oldest first, for
 * $100 of par (R32). The coupon dates step back from the maturity date by
 * the payment frequency, and none falls before the first coupon date. Each
 * coupon is the annual coupon rate (a percent, "4.000" or "4.000%" is 4%) divided by the
 * coupons a year; the last payment adds the $100 of par. A bond with no
 * coupon pays the $100 at maturity.
 *
 * Null when the terms cannot give the schedule: an empty or unreadable
 * maturity or coupon rate, or, for a coupon that is not 0%, payment frequency.
 * An empty list when the bond matured on or before `asOf`.
 */
export function bondCashFlows({
  rate,
  paymentFrequency,
  maturity,
  firstCoupon,
  asOf,
}: {
  rate: unknown;
  paymentFrequency: unknown;
  maturity: unknown;
  firstCoupon?: unknown;
  asOf: Date;
}): BondCashFlow[] | null {
  const maturityDate = parseBondTermDate(maturity);
  const percent = isEmptyValue(rate)
    ? null
    : Number(String(rate).trim().replace(/%$/, ""));
  const perYear = paymentsPerYear(paymentFrequency);
  if (maturityDate === null || percent === null) return null;
  if (!Number.isFinite(percent) || percent < 0) return null;
  // A 0% coupon pays the par at maturity with no use of the frequency.
  if (perYear === null && percent !== 0) return null;
  if (maturityDate.getTime() <= asOf.getTime()) return [];
  if (!perYear || percent === 0) {
    return [{ date: maturityDate, amount: PAR }];
  }

  const coupon = (PAR * percent) / 100 / perYear;
  const first = parseBondTermDate(firstCoupon);
  const step = 12 / perYear;
  const flows: BondCashFlow[] = [];
  for (let i = 0; i < MAX_PAYMENTS; i++) {
    const date = monthsBefore(maturityDate, i * step);
    if (date.getTime() <= asOf.getTime()) break;
    if (first && date.getTime() < first.getTime()) break;
    flows.push({ date, amount: i === 0 ? coupon + PAR : coupon });
  }
  return flows.reverse();
}
